import React from 'react';

import { ComboboxPopover } from './combobox-popover';
import { ComboboxTray } from './combobox-tray';
import { usePrefersTray } from './use-combobox-presentation';

export interface ComboboxOption {
  disabled?: boolean;
  label: string;
  value: string;
}

/**
 * Generic controlled combobox for caller-filtered options.
 *
 * This composes React Aria directly because BUI 0.15 always applies its own
 * `contains` filter and exposes neither a clear action nor a results-footer
 * slot. Keeping those differences behind NS Theme prevents feature packages
 * from depending on an external UI implementation.
 */
export interface ComboboxProps {
  /** Whether a typed value that matches no option is allowed. */
  allowsCustomValue?: boolean;
  /**
   * Whether an open list stays open without options, to show `emptyText`.
   * Defaults to `true`.
   *
   * @remarks
   * Pass `false` while an empty list would say nothing true, for instance
   * while the answer is still on its way or after it failed. An open list
   * then closes as soon as `options` becomes empty, in both presentations, so
   * whatever the page shows next to the field is reachable again.
   *
   * The default keeps the popover as it was. The tray is not only governed by
   * this prop: an open tray also closes when `isDisabled` becomes true, for
   * every caller and whatever this prop says.
   */
  allowsEmptyCollection?: boolean;
  /**
   * Helper text under the field — the slot Tractie calls help-text and MUI calls
   * helperText. Turns red together with the label when `isInvalid` is set, so an
   * error message passed here reads as one unit with the field.
   */
  description?: string;
  /** Text shown in the list when there are no options. Defaults to "No results found.". */
  emptyText?: string;
  /** A note shown below the list, outside the filtered collection, such as "there are more results" or why options were left out. */
  hint?: string;
  /** The controlled input text. */
  inputValue: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  /** Shows a spinner while the options are being fetched. The field stays usable. */
  isLoading?: boolean;
  isRequired?: boolean;
  /** The accessible label for the field. */
  label?: string;
  onInputChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onSelectionChange: (key: string | null) => void;
  /** Opens the menu on focus (true) or only once the user types (false). */
  openOnFocus?: boolean;
  /** The full option list to show. Passed as react-aria `items` (no client filter). */
  options: readonly ComboboxOption[];
  /** Hint text shown in the empty field, e.g. "Search a group". */
  placeholder?: string;
  /** The controlled selected key, or null when nothing is selected. */
  selectedKey: string | null;
}

type Presentation = 'popover' | 'tray';

// The host only tracks focus, so it stays out of the layout. Inline rather than
// a class: this file would otherwise import a CSS module for one declaration.
const HOST_STYLE: React.CSSProperties = { display: 'contents' };

/**
 * Picks the presentation: the inline popover, or a tray over the page when the
 * visible viewport is short or the pointer is coarse (see `usePrefersTray`).
 * Both presentations take the same props and report through the same callbacks.
 *
 * @remarks
 * An open tray closes when the field becomes disabled, or when it has no
 * options left while `allowsEmptyCollection` is `false`. The popover gets the
 * second rule from react-aria itself. Without them an open tray would keep
 * the rest of the page hidden from assistive technology, and its search box,
 * which `isDisabled` does not reach, would keep accepting input.
 */
export function Combobox(props: ComboboxProps): React.ReactElement {
  const preferred: Presentation = usePrefersTray() ? 'tray' : 'popover';
  const [presentation, setPresentation] =
    React.useState<Presentation>(preferred);
  const [isOpen, setIsOpen] = React.useState(false);
  const [hasFocus, setHasFocus] = React.useState(false);

  const hasNothingToOffer =
    props.isDisabled === true ||
    (props.allowsEmptyCollection === false && props.options.length === 0);
  // Adjusted during render rather than in an effect, so the render that
  // disables the field or empties the list never shows the tray open.
  if (presentation === 'tray' && isOpen && hasNothingToOffer) {
    setIsOpen(false);
  }

  // Switch only when nothing depends on the current markup. While open, the
  // overlay belongs to it; while focused, the element focus returns to on
  // close does. Switching in either state unmounts that element.
  React.useEffect(() => {
    if (!isOpen && !hasFocus) setPresentation(preferred);
  }, [isOpen, hasFocus, preferred]);

  return (
    <div
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setHasFocus(false);
        }
      }}
      onFocus={() => setHasFocus(true)}
      style={HOST_STYLE}
    >
      {presentation === 'tray' ? (
        <ComboboxTray {...props} isOpen={isOpen} onOpenChange={setIsOpen} />
      ) : (
        <ComboboxPopover {...props} onOpenChange={setIsOpen} />
      )}
    </div>
  );
}
